<template>
    <div>

        <vue-headful title="Incident Trends :: MeritEHS"/>

        <h4 class="py-3 mb-4">
            <ol class="breadcrumb font-weight-bold m-0">
                <li class="breadcrumb-item active"><i class="fa fa-stethoscope"></i> Incident Trend</li>
                <li class="ml-3">
                    <router-link :to="`/incidents/create`" class="btn btn-sm btn-success"><i class="fa fa-plus-circle"></i> Create Incident</router-link>
                </li>
            </ol>
        </h4>

        <saved-filter-title :filterKey="filterKey" />

        <div class="card">
            <div class="card-body">
                <v-server-table :name="tableName" :ref="tableName" url="/incident/list" :columns="columns" :options="options">

                    <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 mb-md-0 mb-3 float-md-left">
                        <div class="clearfix d-md-none d-block"></div>

                        <div class="float-left">
                            <table-filters-2 ref="trendTableFilters" filterUrl="/incident/filters" v-bind:filters="filters" :emptyFilters="filterDefs" :filterKey="filterKey" @filtersChanged="refreshTableData" @filtersCleared="filtersCleared">
                                <template slot-scope="slotProps">
                                    <div class="d-inline-block">
                                        <multi-select v-model="slotProps.filters['incident.IncidentTypeId']" :btnLabel="(values) => ('Incident Types ('+values.length+')')" :selectOptions="slotProps.filterData.incidentTypes" :options="{multi: true}"></multi-select>
                                    </div>
                                    <div class="d-inline-block">
                                        <multi-select v-model="slotProps.filters['incident.InjuryTypeId']" :btnLabel="(values) => ('Injury Types ('+values.length+')')" :selectOptions="slotProps.filterData.injuryTypes" :options="{multi: true}"></multi-select>
                                    </div>
                                    <div class="d-inline-block">
                                        <multi-select v-model="slotProps.filters['jobsite.DivisionId']" :btnLabel="(values) => ('Divisions ('+values.length+')')" :selectOptions="slotProps.filterData.divisions" :options="{multi: true}"></multi-select>
                                    </div>
                                    <div class="d-inline-block">
                                        <date-range-selector buttonTitle="Incident Date" v-model="slotProps.filters['incident.IncidentDate']" />
                                    </div>
                                    <div class="d-inline-block" style="width: 300px;">
                                        <jobsite-select :selectedValues="slotProps.filters['incident.JobsiteId']" :forFilters="true" v-on:vselected="((data) => { slotProps.filters['incident.JobsiteId'] = data || {};  })" />
                                    </div>
                                </template>
                            </table-filters-2>
                        </div>

                        <button type="button" class="btn btn-outline-success ml-2" v-on:click="exportIncidents()"><i class="fa fa-file-excel-o"></i> Export</button>

                        <div class="clearfix d-md-none d-block"></div>
                    </div>

                    <router-link slot="IncidentId" slot-scope="props" :to="`/incidents/view/${props.row.IncidentId}`" title="View Incident" class="btn btn-sm btn-light">
                        View - {{ props.row.IncidentId }}
                    </router-link>

                    <template slot="IncidentDate" slot-scope="props">
                        {{ props.row.IncidentDate | moment("MM/DD/YYYY")}}
                    </template>

                    <template slot="IsOshaRecordable" slot-scope="props">
                        <span class="badge badge-danger" v-if="props.row.IsOshaRecordable">
                            Yes
                        </span>
                        <span class="badge badge-default" v-else>
                            No
                        </span>
                    </template>

                    <template slot="IsDotRecordable" slot-scope="props">
                        <span class="badge badge-danger" v-if="props.row.IsDotRecordable">
                            Yes
                        </span>
                        <span class="badge badge-default" v-else>
                            No
                        </span>
                    </template>

                    <template slot="IsTempEmployee" slot-scope="props">
                        <span v-if="props.row.IsTempEmployee">
                            Yes
                        </span>
                        <span v-else>
                            No
                        </span>
                    </template>

                    <template slot="IsContractor" slot-scope="props">
                        <span v-if="props.row.IsContractor">
                            Yes
                        </span>
                        <span v-else>
                            No
                        </span>
                    </template>

                    <template slot="child_row" slot-scope="props">
                        <dl class="dl-horizontal">
                            <dt>Superintendent</dt> <dd><user-name :user="superintendent(props.row)" /></dd>
                            <dt>Project Manager</dt> <dd><user-name :user="project_manager(props.row)" /></dd>
                            <dt>Foreman</dt> <dd>{{ props.row.ForemanFirstName }} {{ props.row.ForemanLastName }}</dd>
                            <dt>Leadman</dt> <dd>{{ props.row.LeadmanFirstName }} {{ props.row.LeadmanLastName }}</dd>
                            <dt>Crew Size</dt> <dd>{{ props.row.CrewSize }}</dd>
                        </dl>
                    </template>
                </v-server-table>
            </div>
        </div>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import customVTColDropdown from '@/components/vue-tables-2/CustomColumnDropdown';

    export default {
        data: function() {
            return {
                tableName: "incidentTrendTable",

                columns: [
                    'IncidentId',
                    'EmpLastName',
                    'EmpFirstName',
                    'IncidentDate',
                    'day_of_week',
                    'employee_duration',
                    'employee_age',
                    'JobsiteNumber',
                    'DivisionName',
                    'IncidentTypeName',
                    'InjuryTypeName',
                    'MajorBodyPartName',
                    'SpecificBodyPartName',
                    'IsOshaRecordable',
                    'IsDotRecordable',
                    'IsTempEmployee',
                    'IsContractor',
                ],

                filterKey: 'trendFilters',

                filterDefs: {
                    'incident.IncidentTypeId': [],
                    'incident.InjuryTypeId': [],
                    'jobsite.DivisionId': [],
                    'incident.IncidentDate' : '',
                    'incident.JobsiteId' : {},
                },
            }
        },

        watch: {
            appliedFilter(newVal, oldVal){
                //Call the helper
                this.checkForAppliedFilter();
            },
        },

        computed: {
            filters(){
                //Check State
                if(this.$store.state.tableFilters.filters[this.filterKey])
                {
                    //Send it
                    return this.$store.state.tableFilters.filters[this.filterKey];
                }

                //Return Definition
                return JSON.parse(JSON.stringify(this.filterDefs));
            },

            appliedFilter(){
                //Send it
                return this.$store.state.appliedFilters[this.filterKey] ? this.$store.state.appliedFilters[this.filterKey] : null;
            },

            options(){
                return {
                    headings: {
                        IncidentId: 'ID',
                        EmpLastName: 'Last Name',
                        EmpFirstName: 'First Name',
                        IncidentDate: 'Date',
                        day_of_week: 'Weekday',
                        employee_duration: 'Emp. Tenure',
                        employee_age: 'Emp. Age',
                        JobsiteNumber: 'Jobsite#',
                        DivisionName: 'Division',
                        IncidentTypeName: 'Incident Type',
                        InjuryTypeName: 'Injury Type',
                        MajorBodyPartName: 'Major Body Part',
                        SpecificBodyPartName: 'Specific Body Part',
                        IsOshaRecordable: 'OSHA Rec.',
                        IsDotRecordable: 'DOT Rec.',
                        IsTempEmployee: 'Employee',
                        IsContractor: 'Contractor',
                    },
                    saveState: true,
                    columnsDropdown: customVTColDropdown,
                    visibleColumns: this.$root.getVisibleColumnsFromState(this.tableName),
                    orderBy: {
                        ascending: false,
                        column: 'incident.IncidentDate',
                    },
                    requestAdapter: (data) => {
                        //INclude the asset
                        data.assetType = 'trend';

                        //Set the filters
                        data.filters = filterExtractor.extractFilters(this.filters);

                        //Send it
                        return data;
                    },
                    uniqueKey: 'IncidentId',
                };
            },
        },

        methods: {
            superintendent: function(data) {
                return {
                    FirstName: data.SuperintendentFirstName,
                    LastName: data.SuperintendentLastName,
                    deleted_at: data.SuperintendentDeletedAt,
                };
            },

            project_manager: function(data) {
                return {
                    FirstName: data.ProjectManagerFirstName,
                    LastName: data.ProjectManagerLastName,
                    deleted_at: data.ProjectManagerDeletedAt,
                };
            },

            exportIncidents(){
            //Loading indicator
            this.exportingTable = true;

            //Build the payload
            var payload = {
                filters: filterExtractor.extractFilters(this.filters),
                query: (this.$refs.incidentTrendTable.getRequestParams()).query,
                orderBy: this.$refs.incidentTrendTable.options.orderBy.column,
                ascending: this.$refs.incidentTrendTable.options.orderBy.ascending,
                table: 'incident-trend-table',
                columns: this.$refs.incidentTrendTable.columns,
                visible_columns: this.$root.getVisibleColumnsFromState(this.tableName),
                assetType: 'trend',
            };

            //Send it
            this.$http.post('/incident/export', payload)
                .then(response => {
                    //Open the new window
                    window.open(response.data);
                })
                .catch(err => {})
                .then(response => {
                    //reset
                    this.exportingTable = false;
                });
            },

            refreshTableData(filters) {
                //Set the data
                this.$refs.incidentTrendTable.setPage(1);
                this.$refs.incidentTrendTable.getData();
            },

            filtersCleared(){
                //Reset
                this.refreshTableData();

                //Reset the Columns
                this.$refs.trendTableFilters.resetColVis(this.$refs.incidentTrendTable);
            },

            // setUserFilterData(){
            //     //Call the Helper
            //     this.$refs.inspSavedFilters.setFilterParams(this.$refs.incidentTrendTable);
            // },

            checkForAppliedFilter(){
                //Check if
                if(!this.allowFilterSaving || !this.appliedFilter)
                {
                    return;
                }

                //Call the helper
                this.$refs.trendTableFilters.setColVis(this.$refs.incidentTrendTable);
            },
        },

        mounted(){
            //Focus on the SEarch Input
            this.$refs.incidentTrendTable.$refs.genericFilter.focus();

            //Check for filters
            this.checkForAppliedFilter();
        },
    }
</script>
